import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';

import { API_BASE_URL } from '../../config';
import {
  answerKnowledgeHubQuestion,
  getKnowledgeHubConversation,
  getLatestKnowledgeHubConversation,
  listKnowledgeHubConversations,
  runKnowledgeHubAgent,
  sendKnowledgeHubAgentFeedback,
  searchKnowledgeHubHybrid,
} from './api';
import axiosInstance from '../../services/axiosInstance';
import {
  KnowledgeHubAnswerActiveContext,
  KnowledgeHubAnswerActiveResultSet,
  KnowledgeHubAnswerHistoryItem,
  KnowledgeHubAnswerResponse,
  KnowledgeHubAgentResponse,
  KnowledgeHubAgentContextState,
  KnowledgeHubAgentDepth,
  KnowledgeHubAgentSource,
  KnowledgeHubAgentStreamEvent,
  KnowledgeHubAgentToolExecution,
  KnowledgeHubConversationDetail,
  KnowledgeHubConversationSummary,
  KnowledgeHubEntity,
  KnowledgeHubHybridSearchResponse,
  KnowledgeHubHybridSearchResult,
  KnowledgeHubResponseConfidence,
} from './types';
import KnowledgeHubEntityCard, {
  downloadKnowledgeHubEntityAsset,
  getKnowledgeHubEntityAssetId,
  openKnowledgeHubEntityAsset,
} from './KnowledgeHubEntityCard';
import KnowledgeHubAnswerContent from './KnowledgeHubAnswerContent';
import KnowledgeDocumentViewer from './KnowledgeDocumentViewer';

interface KnowledgeHubDrawerProps {
  open: boolean;
  onClose: () => void;
  showDebugScores?: boolean;
  anchor?: 'left' | 'right';
}

type ChatTurnStatus = 'running' | 'searching' | 'answering' | 'complete' | 'warning' | 'error';

interface ChatTurn {
  id: string;
  query: string;
  status: ChatTurnStatus;
  answer?: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse;
  search?: KnowledgeHubHybridSearchResponse;
  partialAnswer?: string;
  toolExecutions?: KnowledgeHubAgentToolExecution[];
  sources?: KnowledgeHubAgentSource[];
  progressMessage?: string;
  warningMessage?: string;
  errorMessage?: string;
}

interface OpenPdfDocument {
  url: string;
  fileName: string;
  pageNumber?: number;
  objectUrl?: boolean;
}

const MIN_VISIBLE_RESULT_SCORE = 0.5;
const DEFAULT_CARD_LIMIT = 6;
const DEFAULT_GROUP_CARD_LIMIT = 3;
const DEFAULT_RESULT_LIST_LIMIT = 12;
const ACTIVE_CONVERSATION_STORAGE_KEY = 'knowledgeHubActiveConversationId';

function getErrorMessage(error: unknown): string {
  const anyError = error as any;
  return anyError?.response?.data?.error
    || anyError?.response?.data?.message
    || anyError?.message
    || 'Athena-anropet misslyckades.';
}

function absoluteBackendUrl(path?: string): string {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function getEntityPreviewUrl(entity: KnowledgeHubEntity): string {
  const previewPath = entity.metadata?.previewPaths?.medium
    || entity.metadata?.previewPaths?.thumbnail
    || entity.metadata?.assetUrl;
  return absoluteBackendUrl(previewPath);
}

function getDocumentFileName(entity: KnowledgeHubEntity): string {
  return entity.metadata?.fileName || entity.title || 'dokument.pdf';
}

function getKnowledgeHubDocumentSourcePath(entity: KnowledgeHubEntity): string {
  return `/api/knowledge-hub/documents/${encodeURIComponent(entity._id)}/source`;
}

function getDocumentViewerPath(entity: KnowledgeHubEntity): string {
  const metadata = entity.metadata || {};
  if (metadata.downloadUrl) return metadata.downloadUrl;
  if (metadata.sourceUrl) return metadata.sourceUrl;
  const assetId = getKnowledgeHubEntityAssetId(entity);
  if (assetId) return `/api/assets/${encodeURIComponent(assetId)}/download`;
  if (['document', 'salesSheet', 'brandGuide'].includes(entity.entityType)) {
    return getKnowledgeHubDocumentSourcePath(entity);
  }
  return '';
}

function isImageLikeEntity(entity: KnowledgeHubEntity): boolean {
  const metadata = entity.metadata || {};
  const fileName = `${metadata.fileName || entity.title || ''}`.toLowerCase();
  const fileType = `${metadata.fileType || ''}`.toLowerCase();
  return entity.entityType === 'image'
    || fileType.startsWith('image/')
    || /\.(jpe?g|png|webp|gif|tif|tiff)$/i.test(fileName)
    || Boolean(metadata.previewPaths?.thumbnail || metadata.previewPaths?.medium);
}

function isDocumentLikeResult(result: KnowledgeHubHybridSearchResult): boolean {
  const entityType = result.entity.entityType;
  const chunkType = String(result.chunk?.metadata?.chunkType || '');
  return ['document', 'salesSheet', 'brandGuide'].includes(entityType)
    || ['documentText', 'valuableText', 'profileSummary'].includes(chunkType);
}

function queryLooksVisual(query: string): boolean {
  const normalized = query.toLowerCase();
  return /\b(bild|bilder|foto|fotografi|miljöbild|miljöbilder|sommarbild|kampanjbild|produktbild|matfoto|kock|kök|förkläde|scen|miljö|blått|blå|grön|grönt|röd|rött|logo|logotyp|logotyper|logga|loggor|logotype|ikon|ikoner|ean|streckkod|qr|butiksbild|eventbild|beställmaterial|säljmaterial|tryckoriginal)\b/.test(normalized);
}

function queryLooksDocumentFocused(query: string): boolean {
  const normalized = query.toLowerCase();
  return /\b(dokument|pdf|katalog|produktkatalog|vinkatalog|säljblad|saljblad|manual|brand guide|grafisk manual)\b/.test(normalized);
}

function queryLooksListRequest(query: string): boolean {
  const normalized = normalizeReferenceText(query);
  return [
    /\b(lista|listar|listadessa|lista dessa|gör en lista|gor en lista|göra en lista|gora en lista|kan du lista|visa alla|hitta alla|alla)\b/,
    /\b(punktform|punktlista|punkter|bullet|bullets)\b/,
    /\b(vilka|vilken|vilket)\s+(av|är|ar)\b/,
    /\b(flera|samtliga)\b/,
  ].some((pattern) => pattern.test(normalized));
}

function getPrimaryResultType(results: KnowledgeHubHybridSearchResult[]): string {
  const counts = new Map<string, number>();
  results.forEach((result) => {
    const type = result.entity.entityType || 'träff';
    counts.set(type, (counts.get(type) || 0) + 1);
  });
  return Array.from(counts.entries()).sort((left, right) => right[1] - left[1])[0]?.[0] || 'träff';
}

function getResultTypeLabel(type: string, count: number): string {
  const labels: Record<string, [string, string]> = {
    asset: ['asset', 'assets'],
    product: ['produkt', 'produkter'],
    wine: ['vin', 'viner'],
    recipe: ['recept', 'recept'],
    image: ['bild', 'bilder'],
    category: ['kategori', 'kategorier'],
    document: ['dokument', 'dokument'],
    salesSheet: ['säljblad', 'säljblad'],
    brandGuide: ['manual', 'manualer'],
  };
  const [singular, plural] = labels[type] || ['träff', 'träffar'];
  return count === 1 ? singular : plural;
}

function normalizeReferenceText(value: unknown): string {
  return String(value || '')
    .toLocaleLowerCase('sv-SE')
    .replace(/[^0-9a-zåäö]+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getActiveResultSetSummary(activeResultSet: KnowledgeHubAnswerActiveResultSet | null): string {
  const items = activeResultSet?.items || [];
  if (items.length === 0) return '';

  const counts = new Map<string, number>();
  items.forEach((item) => {
    const type = item.entityType || 'träff';
    counts.set(type, (counts.get(type) || 0) + 1);
  });
  const primaryType = Array.from(counts.entries()).sort((left, right) => right[1] - left[1])[0]?.[0] || 'träff';
  return `${items.length} ${getResultTypeLabel(primaryType, items.length)} från senaste lista`;
}

function hasStructuredListResults(results: KnowledgeHubHybridSearchResult[]): boolean {
  return results.some((result) => (result.matchReasons || []).includes('structured list'));
}

function isUserDisplayEligibleResult(result: KnowledgeHubHybridSearchResult): boolean {
  return String(result.chunk?.metadata?.displayEligibility || 'show') !== 'contextOnly';
}

function getRelevantResults(results: KnowledgeHubHybridSearchResult[], query = ''): KnowledgeHubHybridSearchResult[] {
  if (hasStructuredListResults(results)) return results;
  const displayableResults = results.filter(isUserDisplayEligibleResult);
  const documentResults = displayableResults.filter(isDocumentLikeResult);
  if (queryLooksDocumentFocused(query) && documentResults.length > 0) return documentResults.slice(0, DEFAULT_CARD_LIMIT);
  const aboveThreshold = displayableResults.filter((result) => result.score >= MIN_VISIBLE_RESULT_SCORE);
  if (aboveThreshold.length > 0) return aboveThreshold;
  return displayableResults.slice(0, 1);
}

function getResultIdentity(result: KnowledgeHubHybridSearchResult): string {
  return `${result.entity._id}:${result.chunk?.id || ''}`;
}

function getConfidenceLevel(confidence?: KnowledgeHubResponseConfidence): string {
  if (!confidence) return '';
  return typeof confidence === 'string' ? confidence : confidence.level;
}

function responseStartsNewTopic(response: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse): boolean {
  return response.turnInfo?.newTopic === true || response.turnInfo?.isNewTopic === true;
}

function extractCitedReferenceNumbers(answer: string): number[] {
  return Array.from(new Set(Array.from(String(answer || '').matchAll(/\[(\d+)\]/g))
    .map((match) => Number(match[1]))
    .filter((value) => Number.isFinite(value) && value > 0)));
}

function getSourceReferenceNumbers(response: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse | undefined): number[] {
  const usedSourceIds = new Set(response?.usedSourceIds || []);
  if (usedSourceIds.size === 0) return [];
  const sources = (response?.sources || []) as Array<{ sourceId?: string; referenceNumber: number }>;
  return Array.from(new Set(sources
    .filter((source) => source.sourceId && usedSourceIds.has(source.sourceId))
    .map((source) => source.referenceNumber)
    .filter((value) => Number.isFinite(value) && value > 0)));
}

function queryLikelyRequiresSources(query: string): boolean {
  const normalized = normalizeReferenceText(query);
  if (/^(hej|hallå|halla|god morgon|godmiddag|god kväll|hur mår du|hur mar du)\b/.test(normalized)) return false;
  return /\b(vad|vilka|vilken|hur|varför|varfor|beskriv|beskrivning|copy|copytext|sammanfatta|jämför|jamfor|lista|visa|hitta|antal|råvara|ravara|hantverk|produkt|recept|dokument|bild)\b/.test(normalized);
}

type ResponseSource = KnowledgeHubAnswerResponse['sources'][number] | KnowledgeHubAgentResponse['sources'][number];

function resultMatchesResponseSource(result: KnowledgeHubHybridSearchResult, source: ResponseSource): boolean {
  if (source.sourceId && result.sourceId) return result.sourceId === source.sourceId;
  if ('chunkId' in source && source.chunkId && result.chunk?.id) return result.chunk.id === source.chunkId;
  return result.entity._id === source.entityId;
}

function getResponseSourceForResult(
  response: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse | undefined,
  result: KnowledgeHubHybridSearchResult,
): ResponseSource | undefined {
  const sources = (response?.sources || []) as ResponseSource[];
  if (result.sourceId) {
    const sourceIdMatch = sources.find((source) => source.sourceId === result.sourceId);
    if (sourceIdMatch) return sourceIdMatch;
  }
  if (result.chunk?.id) {
    const chunkMatch = sources.find((source) => 'chunkId' in source && source.chunkId === result.chunk?.id);
    if (chunkMatch) return chunkMatch;
  }
  return sources.find((source) => resultMatchesResponseSource(result, source));
}

function getResultSourceId(
  result: KnowledgeHubHybridSearchResult,
  response?: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse,
): string | undefined {
  return result.sourceId || getResponseSourceForResult(response, result)?.sourceId;
}

function getReferenceNumberForResult(
  result: KnowledgeHubHybridSearchResult,
  allResults: KnowledgeHubHybridSearchResult[],
  response?: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse,
): number {
  const explicitReference = result.referenceNumber || getResponseSourceForResult(response, result)?.referenceNumber;
  if (explicitReference && Number.isFinite(explicitReference) && explicitReference > 0) return explicitReference;
  return allResults.findIndex((item) => getResultIdentity(item) === getResultIdentity(result)) + 1;
}

function getReferencedResults(
  response: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse | undefined,
  results: KnowledgeHubHybridSearchResult[],
): KnowledgeHubHybridSearchResult[] {
  const usedSourceIds = new Set(response?.usedSourceIds || []);
  if (usedSourceIds.size === 0) return [];
  const seen = new Set<string>();
  return results
    .filter((result) => {
      const sourceId = getResultSourceId(result, response);
      return !!sourceId && usedSourceIds.has(sourceId);
    })
    .filter((result) => {
      const key = getResultIdentity(result);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

type MaterialGroupKey = 'images' | 'products' | 'documents' | 'other';

function getMaterialGroupKey(result: KnowledgeHubHybridSearchResult): MaterialGroupKey {
  if (isDocumentLikeResult(result)) return 'documents';
  if (isImageLikeEntity(result.entity)) return 'images';
  if (['product', 'wine'].includes(result.entity.entityType)) return 'products';
  return 'other';
}

function getMaterialGroupLabel(group: MaterialGroupKey): string {
  const labels: Record<MaterialGroupKey, string> = {
    images: 'Bilder',
    products: 'Produkter',
    documents: 'Dokument',
    other: 'Övrigt',
  };
  return labels[group];
}

function getMaterialSectionTitle(results: KnowledgeHubHybridSearchResult[]): string {
  if (results.length === 0) return 'Relaterat material';
  const groups = new Set(results.map(getMaterialGroupKey));
  if (groups.size !== 1) return 'Relaterat material';
  return getMaterialGroupLabel(Array.from(groups)[0]);
}

function getReferenceResult(
  results: KnowledgeHubHybridSearchResult[],
  referenceNumber: number,
  response?: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse,
): KnowledgeHubHybridSearchResult | undefined {
  if (!Number.isFinite(referenceNumber) || referenceNumber < 1) return undefined;
  const explicitResult = results.find((result) => (
    getReferenceNumberForResult(result, results, response) === referenceNumber
  ));
  if (explicitResult) return explicitResult;
  return results[referenceNumber - 1];
}

function openSourceReference(
  results: KnowledgeHubHybridSearchResult[],
  referenceNumber: number,
  onOpenDocument?: (entity: KnowledgeHubEntity, pageNumber?: number) => void,
  response?: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse,
): void {
  const result = getReferenceResult(results, referenceNumber, response);
  const responseSource = result ? getResponseSourceForResult(response, result) : undefined;
  const pageNumber = typeof responseSource?.pageNumber === 'number'
    ? responseSource.pageNumber
    : typeof result?.chunk?.metadata?.pageNumber === 'number'
      ? result.chunk.metadata.pageNumber
      : undefined;
  if (result && getKnowledgeHubEntityAssetId(result.entity)) {
    const fileType = String(result.entity.metadata?.fileType || '').toLocaleLowerCase('sv-SE');
    const fileName = String(result.entity.metadata?.fileName || result.entity.title || '').toLocaleLowerCase('sv-SE');
    if ((fileType.includes('pdf') || fileName.endsWith('.pdf')) && onOpenDocument) {
      onOpenDocument(result.entity, pageNumber);
    } else {
      openKnowledgeHubEntityAsset(result.entity);
    }
    return;
  }
  if (result && onOpenDocument && getDocumentViewerPath(result.entity)) {
    onOpenDocument(result.entity, pageNumber);
    return;
  }
  const element = document.getElementById(`kh-source-${referenceNumber}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

function isAgentResponse(response: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse | undefined): response is KnowledgeHubAgentResponse {
  return !!response && typeof (response as KnowledgeHubAgentResponse).runId === 'string';
}

function openSourceReferenceFromResponse(
  response: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse | undefined,
  results: KnowledgeHubHybridSearchResult[],
  referenceNumber: number,
  onOpenDocument?: (entity: KnowledgeHubEntity, pageNumber?: number) => void,
): void {
  if (isAgentResponse(response)) {
    const source = response.sources.find((candidate) => candidate.referenceNumber === referenceNumber);
    const result = source
      ? results.find((candidate) => resultMatchesResponseSource(candidate, source))
      : undefined;
    if (result) {
      if (onOpenDocument && getDocumentViewerPath(result.entity) && entityShouldOpenInPdfViewer(result.entity)) {
        onOpenDocument(result.entity, source?.pageNumber);
        return;
      }
      openSourceReference(results, referenceNumber, onOpenDocument, response);
      return;
    }
  }
  openSourceReference(results, referenceNumber, onOpenDocument, response);
}

const ReferenceLinkedText: React.FC<{
  text: string;
  results: KnowledgeHubHybridSearchResult[];
  variant?: 'body1' | 'body2';
  sx?: Record<string, unknown>;
  onOpenDocument?: (entity: KnowledgeHubEntity) => void;
}> = ({ text, results, variant = 'body1', sx, onOpenDocument }) => {
  const parts = String(text || '').split(/(\[\d+\])/g).filter((part) => part.length > 0);
  return (
    <Typography variant={variant} sx={sx}>
      {parts.map((part, index) => {
        const match = part.match(/^\[(\d+)\]$/);
        if (!match) return <React.Fragment key={`${index}-${part}`}>{part}</React.Fragment>;
        const referenceNumber = Number(match[1]);
        const hasReference = !!getReferenceResult(results, referenceNumber);
        return (
          <Button
            key={`${index}-${part}`}
            size="small"
            variant="text"
            disabled={!hasReference}
            onClick={() => openSourceReference(results, referenceNumber, onOpenDocument)}
            sx={{
              minWidth: 0,
              p: 0,
              mx: 0.2,
              fontWeight: 850,
              textTransform: 'none',
              verticalAlign: 'baseline',
              lineHeight: 'inherit',
            }}
          >
            {part}
          </Button>
        );
      })}
    </Typography>
  );
};

function buildActiveContext(entity: KnowledgeHubEntity): KnowledgeHubAnswerActiveContext {
  return {
    entityId: entity._id,
    assetId: getKnowledgeHubEntityAssetId(entity) || undefined,
    title: entity.title,
    entityType: entity.entityType,
    fileName: entity.metadata?.fileName,
    articleNumbers: entity.articleNumbers || [],
  };
}

function buildActiveResultSet(results: KnowledgeHubHybridSearchResponse['results']): KnowledgeHubAnswerActiveResultSet {
  return {
    items: results.slice(0, 50).map((result) => ({
      entityId: result.entity._id,
      title: result.entity.title,
      entityType: result.entity.entityType,
      fileName: result.entity.metadata?.fileName,
      articleNumbers: result.entity.articleNumbers || [],
      chunkId: result.chunk?.id,
      pageNumber: typeof result.chunk?.metadata?.pageNumber === 'number' ? result.chunk.metadata.pageNumber : undefined,
      sectionTitle: typeof result.chunk?.metadata?.sectionTitle === 'string' ? result.chunk.metadata.sectionTitle : undefined,
    })),
  };
}

function upsertAgentToolExecution(
  executions: KnowledgeHubAgentToolExecution[] = [],
  next: KnowledgeHubAgentToolExecution,
): KnowledgeHubAgentToolExecution[] {
  const index = executions.findIndex((execution) => execution.callId === next.callId);
  if (index < 0) return [...executions, next];
  return executions.map((execution, currentIndex) => currentIndex === index ? next : execution);
}

function upsertAgentSearchResult(
  search: KnowledgeHubHybridSearchResponse | undefined,
  result: KnowledgeHubHybridSearchResult,
  query: string,
): KnowledgeHubHybridSearchResponse {
  const resultKey = `${result.entity._id}:${result.chunk?.id || ''}`;
  const currentResults = search?.results || [];
  const index = currentResults.findIndex((candidate) => (
    `${candidate.entity._id}:${candidate.chunk?.id || ''}` === resultKey
  ));
  const results = index < 0
    ? [...currentResults, result]
    : currentResults.map((candidate, currentIndex) => currentIndex === index ? result : candidate);
  return {
    query,
    count: results.length,
    mode: 'hybrid',
    warnings: search?.warnings || [],
    results,
  };
}

function upsertAgentSource(
  sources: KnowledgeHubAgentSource[] = [],
  next: KnowledgeHubAgentSource,
): KnowledgeHubAgentSource[] {
  const index = sources.findIndex((source) => source.sourceId === next.sourceId);
  if (index < 0) return [...sources, next];
  return sources.map((source, currentIndex) => currentIndex === index ? next : source);
}

function emptySearchResponse(query: string): KnowledgeHubHybridSearchResponse {
  return {
    query,
    count: 0,
    mode: 'hybrid',
    warnings: [],
    results: [],
  };
}

function restoredAgentResponse(
  conversation: KnowledgeHubConversationDetail,
  query: string,
  answer: string,
  messageContextState?: KnowledgeHubAgentContextState,
): KnowledgeHubAgentResponse {
  const storedContextState = messageContextState || conversation.contextState;
  const contextState = storedContextState
    ? {
      ...storedContextState,
      conversationId: conversation.conversationId,
    }
    : undefined;
  const restoredResults: KnowledgeHubHybridSearchResult[] = (contextState?.sources || []).map((source) => ({
    sourceId: source.sourceId,
    referenceNumber: source.referenceNumber,
    score: Number(source.score || 0),
    sources: ['text'],
    matchReasons: ['Återställd källa från konversationen'],
    chunk: source.chunkId ? {
      id: source.chunkId,
      textSnippet: source.excerpt || '',
      metadata: {
        sectionTitle: source.sectionTitle,
        pageNumber: source.pageNumber,
        evidenceType: source.evidenceType,
      },
    } : undefined,
    entity: {
      _id: source.entityId,
      entityType: source.entityType,
      title: source.title,
      source: {
        system: 'athena',
        collection: source.sourceCollection,
        id: source.sourceRecordId,
        assetId: source.assetId,
      },
      metadata: {
        fileName: source.fileName,
        sourceUrl: source.sourceUrl,
        documentType: source.documentType,
        catalogType: source.catalogType,
        pageCount: source.pageCount,
      },
    },
  }));
  return {
    runId: contextState?.turnId || `${conversation.conversationId}-restored`,
    query,
    answer,
    confidence: 'medium',
    clarifyingQuestion: null,
    suggestedRefinements: [],
    provider: 'anthropic',
    model: 'historik',
    depth: 'standard',
    rounds: 0,
    durationMs: 0,
    usage: { inputTokens: 0, outputTokens: 0 },
    sources: contextState?.sources || [],
    usedSourceIds: contextState?.usedSourceIds || [],
    retrievedSourceIds: contextState?.retrievedSourceIds || [],
    evidenceStatus: (contextState?.usedSourceIds?.length || 0) > 0 ? 'grounded' : 'insufficient',
    contextState,
    search: {
      ...emptySearchResponse(query),
      count: restoredResults.length,
      results: restoredResults,
    },
    toolExecutions: [],
  };
}

function turnsFromConversation(conversation: KnowledgeHubConversationDetail): ChatTurn[] {
  const turns: ChatTurn[] = [];
  let latestUserQuery = '';
  conversation.messages.forEach((message, index) => {
    if (message.role === 'user') {
      latestUserQuery = message.content;
      return;
    }
    if (message.role !== 'assistant' || !latestUserQuery) return;
    const answer = restoredAgentResponse(
      conversation,
      latestUserQuery,
      message.content,
      message.contextState,
    );
    turns.push({
      id: `${conversation.conversationId}-${index}`,
      query: latestUserQuery,
      status: 'complete',
      answer,
      search: answer.search,
      partialAnswer: message.content,
      toolExecutions: [],
    });
  });
  return turns;
}

function readActiveConversationId(): string {
  try {
    return window.localStorage.getItem(ACTIVE_CONVERSATION_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function writeActiveConversationId(conversationId: string | undefined): void {
  try {
    if (conversationId) {
      window.localStorage.setItem(ACTIVE_CONVERSATION_STORAGE_KEY, conversationId);
    } else {
      window.localStorage.removeItem(ACTIVE_CONVERSATION_STORAGE_KEY);
    }
  } catch {
    // localStorage can be unavailable in restrictive browser modes.
  }
}

function formatConversationDate(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

const EXAMPLE_QUERIES = [
  'Visa allt om V198',
  'Saganaki',
  'produkter där produktnamn innehåller feta',
  'hitta en sommarbild med lite blått',
];

interface AnswerBlockProps {
  turn: ChatTurn;
  showDebugScores: boolean;
  onAsk: (query: string) => void;
  onUseAsContext: (entity: KnowledgeHubEntity) => void;
}

const AnswerBlock: React.FC<AnswerBlockProps> = ({ turn, showDebugScores, onAsk, onUseAsContext }) => {
  const response = turn.answer;
  const search = response?.search || turn.search;
  const sourceResults = search?.results || [];
  const sourceLookupResponse = response || (turn.sources?.length
    ? ({ sources: turn.sources } as KnowledgeHubAgentResponse)
    : undefined);
  const sourceReferenceNumbers = getSourceReferenceNumbers(response);
  const sourceResultsUsed = getReferencedResults(response, sourceResults);
  const sourceResultKeys = new Set(sourceResultsUsed.map(getResultIdentity));
  const retrievedSourceIds = new Set(response?.retrievedSourceIds || []);
  const retrievedResults = retrievedSourceIds.size > 0
    ? sourceResults.filter((result) => {
      const sourceId = getResultSourceId(result, sourceLookupResponse);
      return !!sourceId && retrievedSourceIds.has(sourceId);
    })
    : sourceResults;
  const relevantResults = getRelevantResults(retrievedResults, turn.query);
  const primaryType = getPrimaryResultType(relevantResults);
  const hasStructuredList = relevantResults.length > 1 && (
    queryLooksListRequest(turn.query)
    || hasStructuredListResults(relevantResults)
  );
  const uncitedRelevantResults = relevantResults.filter((result) => !sourceResultKeys.has(getResultIdentity(result)));
  const hasUncitedResults = uncitedRelevantResults.length > 0;
  const sourceRequiredButUncited = response?.answer
    && queryLikelyRequiresSources(turn.query)
    && sourceReferenceNumbers.length === 0;
  const displayedAnswer = response?.answer || turn.partialAnswer || '';
  const incomplete = !response && ['warning', 'error'].includes(turn.status);
  const [pdfDocument, setPdfDocument] = useState<OpenPdfDocument | null>(null);
  const [pdfError, setPdfError] = useState('');
  const [sourcesExpanded, setSourcesExpanded] = useState(incomplete);
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null);
  const [feedbackError, setFeedbackError] = useState('');

  useEffect(() => () => {
    if (pdfDocument?.objectUrl) URL.revokeObjectURL(pdfDocument.url);
  }, [pdfDocument]);

  useEffect(() => {
    if (incomplete) setSourcesExpanded(true);
  }, [incomplete]);

  const closePdfDocument = () => {
    setPdfDocument((current) => {
      if (current?.objectUrl) URL.revokeObjectURL(current.url);
      return null;
    });
    setPdfError('');
  };

  const openPdfDocument = async (entity: KnowledgeHubEntity, pageNumber?: number) => {
    const path = getDocumentViewerPath(entity);
    if (!path) {
      setPdfError('Dokumentfilen kunde inte hittas.');
      return;
    }
    try {
      setPdfError('');
      if (/^https?:\/\//i.test(path)) {
        setPdfDocument({ url: path, fileName: getDocumentFileName(entity), pageNumber });
        return;
      }
      const response = await axiosInstance.get(path, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(response.data);
      setPdfDocument((current) => {
        if (current?.objectUrl) URL.revokeObjectURL(current.url);
        return { url: objectUrl, fileName: getDocumentFileName(entity), pageNumber, objectUrl: true };
      });
    } catch (error: any) {
      setPdfError(error?.response?.data?.message || error?.message || 'Dokumentet kunde inte öppnas.');
    }
  };

  const submitFeedback = async (rating: 'positive' | 'negative') => {
    if (!response || !isAgentResponse(response)) return;
    try {
      setFeedbackError('');
      await sendKnowledgeHubAgentFeedback(response.runId, rating);
      setFeedback(rating);
    } catch {
      setFeedbackError('Feedbacken kunde inte sparas.');
    }
  };

  return (
    <>
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        bgcolor: '#ffffff',
        borderColor: '#d9e7f7',
        boxShadow: '0 16px 40px rgba(7,47,83,0.08)',
      }}
    >
      <Stack spacing={1.75}>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Chip size="small" color="info" label="AI-svar" />
          {response?.confidence && <Chip size="small" variant="outlined" label={getConfidenceLevel(response.confidence)} />}
          {response && isAgentResponse(response) && (
            <Chip
              size="small"
              variant="outlined"
              label={response.depth === 'deep' ? 'Djup sökning' : 'Standard'}
            />
          )}
          {response?.sources && <Chip size="small" variant="outlined" label={`${sourceResultsUsed.length} använda källor`} />}
          {search?.results && <Chip size="small" variant="outlined" label={`${uncitedRelevantResults.length} övriga träffar`} />}
          {showDebugScores && response && isAgentResponse(response) && response.phaseDurations && (
            <Chip
              size="small"
              variant="outlined"
              label={`Plan ${response.phaseDurations.planningMs} ms · källor ${response.phaseDurations.retrievalMs} ms · svar ${response.phaseDurations.synthesisMs} ms`}
            />
          )}
        </Stack>
        {search?.warnings && search.warnings.length > 0 && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Semantisk sökning är tillfälligt begränsad. Athena visar verifierade textträffar.
            {showDebugScores ? ` (${search.warnings.join(' · ')})` : ''}
          </Alert>
        )}

        {displayedAnswer ? (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              bgcolor: '#f8fbff',
              borderColor: '#c8dcf2',
            }}
          >
            <KnowledgeHubAnswerContent
              markdown={displayedAnswer}
              sources={isAgentResponse(response) ? response.sources : (turn.sources || [])}
              results={sourceResults}
              onSourceClick={(referenceNumber) => openSourceReferenceFromResponse(
                sourceLookupResponse,
                sourceResults,
                referenceNumber,
                (entity, pageNumber) => void openPdfDocument(entity, pageNumber),
              )}
            />
          </Paper>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Jag söker i Athena-källorna och bygger ett svar.
          </Typography>
        )}
        {response && isAgentResponse(response) && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary">Var svaret hjälpsamt?</Typography>
            <IconButton
              size="small"
              color={feedback === 'positive' ? 'success' : 'default'}
              aria-label="Bra svar"
              onClick={() => void submitFeedback('positive')}
            >
              <ThumbUpOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color={feedback === 'negative' ? 'error' : 'default'}
              aria-label="Dåligt svar"
              onClick={() => void submitFeedback('negative')}
            >
              <ThumbDownOutlinedIcon fontSize="small" />
            </IconButton>
            {feedbackError && <Typography variant="caption" color="error">{feedbackError}</Typography>}
          </Stack>
        )}

        {incomplete && (
          <Alert severity={turn.status === 'error' ? 'error' : 'warning'} sx={{ borderRadius: 2 }}>
            Körningen slutfördes inte. Delvis svar och redan hämtade träffar visas nedan.
            {turn.errorMessage ? ` ${turn.errorMessage}` : ''}
          </Alert>
        )}

        {response?.appliedFilters && response.appliedFilters.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              Tolkat som
            </Typography>
            {response.appliedFilters.map((filterLabel) => (
              <Chip key={filterLabel} size="small" variant="outlined" label={filterLabel} sx={{ bgcolor: '#ffffff' }} />
            ))}
          </Stack>
        )}

        {hasStructuredList && (
          <StructuredResultList
            results={relevantResults}
            primaryType={primaryType}
            onOpenDocument={(entity) => void openPdfDocument(entity)}
          />
        )}

        {sourceRequiredButUncited && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Svaret citerade inga verifierade källor. Be gärna Athena hämta källor om svaret ska användas som faktaunderlag.
          </Alert>
        )}

        {sourceResultsUsed.length > 0 && (
          <SourceResults
            query={turn.query}
            results={sourceResults}
            displayResults={sourceResultsUsed}
            response={sourceLookupResponse}
            title="Källor som användes i svaret"
            description="Kort källaöversikt. Källnumren visas här så att svaret ovan är lätt att kopiera utan markeringar."
            referenceLabel="Källa"
            showDebugScores={showDebugScores}
            onUseAsContext={onUseAsContext}
            onOpenDocument={(entity, pageNumber) => void openPdfDocument(entity, pageNumber)}
            initiallyExpanded
          />
        )}

        {response?.clarifyingQuestion && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {response.clarifyingQuestion}
          </Alert>
        )}

        {turn.warningMessage && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {turn.warningMessage}
          </Alert>
        )}

        {response?.suggestedRefinements && response.suggestedRefinements.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {response.suggestedRefinements.map((refinement) => (
              <Chip
                key={refinement}
                size="small"
                variant="outlined"
                label={refinement}
                onClick={() => onAsk(refinement)}
                sx={{ cursor: 'pointer', bgcolor: '#f8fbff' }}
              />
            ))}
          </Stack>
        )}

        {search?.results && hasUncitedResults && !sourcesExpanded && (
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, bgcolor: '#ffffff' }}>
            <Stack spacing={1}>
              <Button
                size="small"
                variant="text"
                onClick={() => setSourcesExpanded((current) => !current)}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 850 }}
              >
                {sourcesExpanded ? 'Dölj övriga träffar' : `Visa övriga träffar Athena hittade (${uncitedRelevantResults.length})`}
              </Button>
            </Stack>
          </Paper>
        )}
        {search?.results && hasUncitedResults && sourcesExpanded && (
          <Stack spacing={1}>
            <Button
              size="small"
              variant="text"
              onClick={() => setSourcesExpanded(false)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 850 }}
            >
              Dölj övriga träffar
            </Button>
            <SourceResults
              query={turn.query}
              results={search.results}
              displayResults={uncitedRelevantResults}
              response={sourceLookupResponse}
              title="Övriga träffar Athena hittade"
              description="Det här är arbetsmaterial eller relaterat material. Det behöver inte ha använts som källa i svaret."
              referenceLabel="Övrig träff"
              showDebugScores={showDebugScores}
              onUseAsContext={onUseAsContext}
              onOpenDocument={(entity, pageNumber) => void openPdfDocument(entity, pageNumber)}
            />
          </Stack>
        )}
      </Stack>
    </Paper>
    <Dialog open={!!pdfDocument} onClose={closePdfDocument} fullScreen PaperProps={{ sx: { bgcolor: '#111827' } }}>
      {pdfDocument && (
        <Box sx={{ height: '100vh', p: 2 }}>
          <KnowledgeDocumentViewer
            url={pdfDocument.url}
            fileName={pdfDocument.fileName}
            pageNumber={pdfDocument.pageNumber}
          />
        </Box>
      )}
    </Dialog>
    {pdfError && (
      <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
        {pdfError}
      </Alert>
    )}
    </>
  );
};

interface StructuredResultListProps {
  results: KnowledgeHubHybridSearchResult[];
  primaryType: string;
  onOpenDocument: (entity: KnowledgeHubEntity) => void;
}

function entityShouldOpenInPdfViewer(entity: KnowledgeHubEntity): boolean {
  const fileType = String(entity.metadata?.fileType || '').toLocaleLowerCase('sv-SE');
  const fileName = String(entity.metadata?.fileName || entity.title || '').toLocaleLowerCase('sv-SE');
  return fileType.includes('pdf') || fileName.endsWith('.pdf') || ['document', 'salesSheet', 'brandGuide'].includes(entity.entityType);
}

const StructuredResultList: React.FC<StructuredResultListProps> = ({ results, primaryType, onOpenDocument }) => {
  const [expanded, setExpanded] = useState(false);
  const visibleResults = expanded ? results : results.slice(0, DEFAULT_RESULT_LIST_LIMIT);
  const remainingCount = Math.max(0, results.length - visibleResults.length);
  const label = getResultTypeLabel(primaryType, results.length);
  const title = getMaterialSectionTitle(results);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#ffffff' }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
            {title}
          </Typography>
          <Chip size="small" variant="outlined" label={`${results.length} visas`} />
        </Stack>
        <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
          {visibleResults.map((result) => (
            <Box component="li" key={result.entity._id} sx={{ pl: 0.5, mb: 0.75 }}>
              {getKnowledgeHubEntityAssetId(result.entity) || getDocumentViewerPath(result.entity) ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    if (entityShouldOpenInPdfViewer(result.entity) && getDocumentViewerPath(result.entity)) {
                      onOpenDocument(result.entity);
                    } else {
                      openKnowledgeHubEntityAsset(result.entity);
                    }
                  }}
                  sx={{
                    p: 0,
                    minWidth: 0,
                    fontWeight: 800,
                    textTransform: 'none',
                    verticalAlign: 'baseline',
                    justifyContent: 'flex-start',
                  }}
                >
                  {result.entity.title}
                </Button>
              ) : (
                <Typography component="span" variant="body2" sx={{ fontWeight: 800 }}>
                  {result.entity.title}
                </Typography>
              )}
              {result.entity.articleNumbers?.[0] && (
                <Typography component="span" variant="body2" color="text.secondary">
                  {' '}({result.entity.articleNumbers[0]})
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        {remainingCount > 0 && (
          <Button size="small" variant="text" onClick={() => setExpanded(true)} sx={{ alignSelf: 'flex-start' }}>
            Visa fler {label} ({remainingCount} till)
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

interface SourceResultsProps {
  query: string;
  results: KnowledgeHubHybridSearchResult[];
  displayResults?: KnowledgeHubHybridSearchResult[];
  response?: KnowledgeHubAnswerResponse | KnowledgeHubAgentResponse;
  title: string;
  description?: string;
  referenceLabel: string;
  showDebugScores: boolean;
  onUseAsContext: (entity: KnowledgeHubEntity) => void;
  onOpenDocument: (entity: KnowledgeHubEntity, pageNumber?: number) => void;
  initiallyExpanded?: boolean;
}

const SourceResults: React.FC<SourceResultsProps> = ({
  query,
  results,
  displayResults,
  response,
  title,
  description,
  referenceLabel,
  showDebugScores,
  onUseAsContext,
  onOpenDocument,
  initiallyExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const referenceNumberForResult = (result: KnowledgeHubHybridSearchResult) => (
    getReferenceNumberForResult(result, results, response)
  );
  const baseResults = displayResults || getRelevantResults(results, query);
  const hiddenLowScoreCount = Math.max(0, results.length - baseResults.length);
  const groups = (['images', 'products', 'documents', 'other'] as MaterialGroupKey[])
    .map((group) => ({
      group,
      label: getMaterialGroupLabel(group),
      results: baseResults.filter((result) => getMaterialGroupKey(result) === group),
    }))
    .filter((group) => group.results.length > 0);
  const hasMultipleGroups = groups.length > 1;

  if (baseResults.length === 0) {
    return null;
  }

  const renderedCount = groups.reduce((sum, group) => (
    sum + (expanded ? group.results.length : Math.min(group.results.length, DEFAULT_GROUP_CARD_LIMIT))
  ), 0);
  const remainingCount = Math.max(0, baseResults.length - renderedCount);

  const renderResultCard = (result: KnowledgeHubHybridSearchResult) => {
    const referenceNumber = referenceNumberForResult(result);
    const group = getMaterialGroupKey(result);
    if (group === 'documents') {
      return (
        <DocumentSourceCard
          result={result}
          referenceNumber={referenceNumber}
          referenceLabel={referenceLabel}
          showDebugScores={showDebugScores}
          onUseAsContext={onUseAsContext}
          onOpenDocument={(entity, pageNumber) => onOpenDocument(
            entity,
            pageNumber ?? getResponseSourceForResult(response, result)?.pageNumber,
          )}
        />
      );
    }
    if (group === 'images') {
      return (
        <ImageGridCard
          result={result}
          referenceNumber={referenceNumber}
          referenceLabel={referenceLabel}
          showDebugScores={showDebugScores}
          onUseAsContext={onUseAsContext}
        />
      );
    }
    return (
      <KnowledgeHubEntityCard
        entity={result.entity}
        compact
        referenceNumber={referenceNumber}
        referenceLabel={referenceLabel}
        score={result.score}
        showScore={showDebugScores}
        onUseAsContext={onUseAsContext}
      />
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.35, borderRadius: 2.5, bgcolor: '#ffffff', borderColor: '#d7e3f1' }}>
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
          {title}
        </Typography>
        <Chip size="small" variant="outlined" label={`${baseResults.length} visas`} />
        {showDebugScores && hiddenLowScoreCount > 0 && (
          <Chip size="small" variant="outlined" label={`${hiddenLowScoreCount} svaga dolda`} />
        )}
      </Stack>
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          {description}
        </Typography>
      )}

      {groups.map((group) => {
        const visibleResults = expanded ? group.results : group.results.slice(0, DEFAULT_GROUP_CARD_LIMIT);
        return (
          <Stack key={group.group} spacing={0.9}>
            {(hasMultipleGroups || title === 'Relaterat material') && (
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                  {group.label}
                </Typography>
                <Chip size="small" variant="outlined" label={`${group.results.length}`} />
              </Stack>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: visibleResults.length === 1 || group.group === 'documents' ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
                  md: group.group === 'documents' ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
                },
                gap: 1,
                minWidth: 0,
              }}
            >
              {visibleResults.map((result) => (
                <Box key={getResultIdentity(result)} id={`kh-source-${referenceNumberForResult(result)}`} sx={{ minWidth: 0 }}>
                  {renderResultCard(result)}
                </Box>
              ))}
            </Box>
          </Stack>
        );
      })}

      {remainingCount > 0 && (
        <Button size="small" variant="outlined" onClick={() => setExpanded(true)} sx={{ alignSelf: 'flex-start' }}>
          Visa fler träffar ({remainingCount} till)
        </Button>
      )}
    </Stack>
    </Paper>
  );
};

interface ImageResultCardProps {
  result: KnowledgeHubHybridSearchResult;
  referenceNumber: number;
  referenceLabel: string;
  showDebugScores: boolean;
  onUseAsContext: (entity: KnowledgeHubEntity) => void;
  onOpenDocument?: (entity: KnowledgeHubEntity, pageNumber?: number) => void;
}

const DocumentSourceCard: React.FC<ImageResultCardProps> = ({ result, referenceNumber, referenceLabel, showDebugScores, onUseAsContext, onOpenDocument }) => {
  const { entity } = result;
  const metadata = entity.metadata || {};
  const chunkMetadata = result.chunk?.metadata || {};
  const documentType = String(chunkMetadata.documentType || metadata.documentType || entity.entityType || 'dokument');
  const catalogType = String(chunkMetadata.catalogType || metadata.catalogType || '');
  const originalVsProfile = String(chunkMetadata.originalVsProfile || metadata.originalVsProfile || 'original');
  const sourceLabel = originalVsProfile === 'profile' ? 'AI-profil/enrichment' : 'Original dokumenttext';
  const sectionTitle = String(chunkMetadata.sectionTitle || '');
  const pageNumber = typeof chunkMetadata.pageNumber === 'number' ? chunkMetadata.pageNumber : undefined;
  const pageCount = typeof chunkMetadata.pageCount === 'number' ? chunkMetadata.pageCount : undefined;
  const year = metadata.documentYear || metadata.year;
  const weekNumber = metadata.weekNumber;
  const sourceUrl = getDocumentViewerPath(entity);
  const excerpts = Array.isArray((chunkMetadata as any).excerpts)
    ? (chunkMetadata as any).excerpts.slice(0, 3)
    : [];

  return (
    <Paper variant="outlined" sx={{ p: 1.35, borderRadius: 2.5, bgcolor: '#ffffff', borderColor: '#d7e3f1' }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
          <Chip size="small" color="primary" label={`${referenceLabel} [${referenceNumber}]`} />
          <Chip size="small" variant="outlined" label="Dokument" />
          <Chip size="small" variant="outlined" label={documentType} />
          {catalogType && <Chip size="small" variant="outlined" label={catalogType} />}
          <Chip size="small" variant="outlined" label={sourceLabel} />
          {showDebugScores && <Chip size="small" variant="outlined" label={`Score ${result.score.toFixed(2)}`} />}
        </Stack>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 850, lineHeight: 1.28 }}>
            {entity.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[
              sectionTitle ? `Avsnitt: ${sectionTitle}` : '',
              pageNumber ? `Sida ${pageNumber}${pageCount ? ` av ${pageCount}` : ''}` : '',
              year ? `${year}${weekNumber ? ` v${weekNumber}` : ''}` : '',
            ].filter(Boolean).join(' | ')}
          </Typography>
        </Box>

        {result.chunk?.textSnippet && (
          <Typography variant="body2" sx={{ lineHeight: 1.55, color: '#243447' }}>
            {result.chunk.textSnippet}
          </Typography>
        )}
        {excerpts.length > 1 && (
          <Stack spacing={0.75}>
            {excerpts.slice(1).map((excerpt: any, index: number) => (
              <Box key={excerpt.id || index} sx={{ pl: 1, borderLeft: '2px solid #d7e3f1' }}>
                <Typography variant="caption" color="text.secondary">
                  {excerpt.metadata?.sectionTitle || (excerpt.metadata?.pageNumber ? `Sida ${excerpt.metadata.pageNumber}` : `Utdrag ${index + 2}`)}
                </Typography>
                <Typography variant="body2">{excerpt.textSnippet}</Typography>
              </Box>
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={0.75} flexWrap="wrap">
            {showDebugScores && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  window.localStorage.setItem('knowledgeHubInspectorEntityId', entity._id);
                  window.location.hash = 'knowledge-hub';
                }}
              >
                Visa extraherad text
              </Button>
            )}
          {sourceUrl && onOpenDocument && (
            <Button size="small" variant="outlined" onClick={() => onOpenDocument(entity, pageNumber)}>
              Öppna dokument
            </Button>
          )}
          {getKnowledgeHubEntityAssetId(entity) && (
            <Button size="small" variant="outlined" onClick={() => openKnowledgeHubEntityAsset(entity)}>
              Öppna asset
            </Button>
          )}
          <Button size="small" variant="text" onClick={() => onUseAsContext(entity)}>
            Fokusera på denna
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

const ImageGridCard: React.FC<ImageResultCardProps> = ({ result, referenceNumber, referenceLabel, showDebugScores, onUseAsContext }) => {
  const { entity } = result;
  const metadata = entity.metadata || {};
  const previewUrl = getEntityPreviewUrl(entity);
  const assetId = getKnowledgeHubEntityAssetId(entity);
  const visualMatchReason = (result.matchReasons || []).find((reason) => reason.startsWith('Visuell AI-observation'));

  return (
    <Paper variant="outlined" sx={{ p: 1, borderRadius: 2.5, bgcolor: '#ffffff', minWidth: 0, overflow: 'hidden' }}>
      <Stack spacing={1}>
        {previewUrl && (
          <Box
            component="img"
            src={previewUrl}
            alt={entity.title}
            loading="lazy"
            sx={{
              width: '100%',
              height: 112,
              objectFit: 'cover',
              borderRadius: 2,
              bgcolor: '#edf3fa',
            }}
          />
        )}
        <Stack spacing={0.65}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <Chip size="small" color="primary" label={`${referenceLabel} [${referenceNumber}]`} />
            <Chip size="small" variant="outlined" label="Bild" />
            {showDebugScores && <Chip size="small" variant="outlined" label={`Score ${result.score.toFixed(2)}`} />}
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
            {entity.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[metadata.templateName, ...(metadata.categoryNames || [])].filter(Boolean).join(' | ')}
          </Typography>
          {visualMatchReason && (
            <Typography variant="caption" color="primary.main" sx={{ lineHeight: 1.35 }}>
              {visualMatchReason}
            </Typography>
          )}
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            {assetId && (
              <>
                <Button size="small" variant="outlined" onClick={() => openKnowledgeHubEntityAsset(entity)}>
                  Öppna asset
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    void downloadKnowledgeHubEntityAsset(entity);
                  }}
                >
                  Ladda ner
                </Button>
              </>
            )}
            <Button size="small" variant="text" onClick={() => onUseAsContext(entity)}>
              Fokusera på denna
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};

const KnowledgeHubDrawer: React.FC<KnowledgeHubDrawerProps> = ({ open, onClose, showDebugScores = false, anchor = 'right' }) => {
  const [query, setQuery] = useState('');
  const [history, setHistory] = useState<KnowledgeHubAnswerHistoryItem[]>([]);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [activeContext, setActiveContext] = useState<KnowledgeHubAnswerActiveContext | null>(null);
  const [activeResultSet, setActiveResultSet] = useState<KnowledgeHubAnswerActiveResultSet | null>(null);
  const [agentContextState, setAgentContextState] = useState<KnowledgeHubAgentContextState | undefined>(undefined);
  const [conversationLoadAttempted, setConversationLoadAttempted] = useState(false);
  const [restoringConversation, setRestoringConversation] = useState(false);
  const [historyMenuAnchor, setHistoryMenuAnchor] = useState<HTMLElement | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [conversationSummaries, setConversationSummaries] = useState<KnowledgeHubConversationSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [running, setRunning] = useState(false);
  const [agentDepth, setAgentDepth] = useState<KnowledgeHubAgentDepth>('standard');
  const [message, setMessage] = useState<{ type: 'info' | 'warning' | 'error'; text: string } | null>(null);
  const latestTurnRef = useRef<HTMLDivElement | null>(null);
  const activeRunControllerRef = useRef<AbortController | null>(null);

  const busy = running || restoringConversation;
  const hasConversation = turns.length > 0;
  const resultSetSummary = getActiveResultSetSummary(activeResultSet);
  const historyMenuOpen = Boolean(historyMenuAnchor);

  useEffect(() => {
    if (!open || !hasConversation) return;
    window.setTimeout(() => {
      latestTurnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [open, hasConversation, turns, running]);

  const updateTurn = (turnId: string, patch: Partial<ChatTurn>) => {
    setTurns((current) => current.map((turn) => (
      turn.id === turnId ? { ...turn, ...patch } : turn
    )));
  };

  const applyConversation = useCallback((conversation: KnowledgeHubConversationDetail) => {
    const restoredTurns = turnsFromConversation(conversation);
    const nextContextState = conversation.contextState
      ? { ...conversation.contextState, conversationId: conversation.conversationId }
      : undefined;
    setTurns(restoredTurns);
    setHistory((conversation.history || []).slice(-10));
    setAgentContextState(nextContextState);
    setActiveResultSet(conversation.activeResultSet || nextContextState?.activeResultSet || null);
    setActiveContext(null);
    setMessage(null);
    writeActiveConversationId(conversation.conversationId);
  }, []);

  const restoreInitialConversation = useCallback(async () => {
    setRestoringConversation(true);
    try {
      const activeConversationId = readActiveConversationId();
      const conversation = activeConversationId
        ? await getKnowledgeHubConversation(activeConversationId)
        : await getLatestKnowledgeHubConversation();
      if (conversation) applyConversation(conversation);
    } catch (error) {
      setMessage({ type: 'warning', text: `Athena kunde inte återställa senaste chatten: ${getErrorMessage(error)}` });
      writeActiveConversationId(undefined);
    } finally {
      setRestoringConversation(false);
    }
  }, [applyConversation]);

  const loadConversationSummaries = async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      setConversationSummaries(await listKnowledgeHubConversations(20));
    } catch (error) {
      setHistoryError(getErrorMessage(error));
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryDialog = () => {
    setHistoryMenuAnchor(null);
    setHistoryDialogOpen(true);
    void loadConversationSummaries();
  };

  const loadConversationFromHistory = async (conversationId: string) => {
    setRestoringConversation(true);
    setHistoryError('');
    try {
      const conversation = await getKnowledgeHubConversation(conversationId);
      if (!conversation) {
        setHistoryError('Konversationen hittades inte.');
        return;
      }
      applyConversation(conversation);
      setConversationLoadAttempted(true);
      setHistoryDialogOpen(false);
    } catch (error) {
      setHistoryError(getErrorMessage(error));
    } finally {
      setRestoringConversation(false);
    }
  };

  useEffect(() => {
    if (!open || conversationLoadAttempted || running || hasConversation) return;
    setConversationLoadAttempted(true);
    void restoreInitialConversation();
  }, [open, conversationLoadAttempted, running, hasConversation, restoreInitialConversation]);

  const submitQuestion = async (queryOverride?: string) => {
    const nextQuery = String(queryOverride || query).trim();
    if (!nextQuery || busy) return;

    const turnId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const controller = new AbortController();
    activeRunControllerRef.current = controller;
    setQuery('');
    setMessage(null);
    setRunning(true);
    setTurns((current) => [
      ...current,
      { id: turnId, query: nextQuery, status: 'running', toolExecutions: [], partialAnswer: '' },
    ]);

    const handleAgentEvent = (event: KnowledgeHubAgentStreamEvent) => {
      if (event.type === 'tool_started' || event.type === 'tool_completed' || event.type === 'tool_failed') {
        setTurns((current) => current.map((turn) => turn.id === turnId ? {
          ...turn,
          status: 'running',
          toolExecutions: upsertAgentToolExecution(turn.toolExecutions, event.execution),
        } : turn));
      } else if (event.type === 'source_upserted') {
        setTurns((current) => current.map((turn) => turn.id === turnId ? {
          ...turn,
          sources: upsertAgentSource(turn.sources, event.source),
        } : turn));
      } else if (event.type === 'artifact_upserted') {
        setTurns((current) => current.map((turn) => turn.id === turnId ? {
          ...turn,
          search: upsertAgentSearchResult(turn.search, {
            ...event.artifact.result,
            sourceId: event.artifact.sourceId,
          }, nextQuery),
        } : turn));
      } else if (event.type === 'answer_delta') {
        setTurns((current) => current.map((turn) => turn.id === turnId ? {
          ...turn,
          partialAnswer: `${turn.partialAnswer || ''}${event.delta}`,
        } : turn));
      } else if (event.type === 'run_progress') {
        setTurns((current) => current.map((turn) => turn.id === turnId ? {
          ...turn,
          status: event.stage === 'answering' ? 'answering' : 'running',
          progressMessage: event.message,
        } : turn));
      } else if (event.type === 'run_completed') {
        const nextContextState = event.response.contextState
          ? {
            ...event.response.contextState,
            activeResultSet: responseStartsNewTopic(event.response)
              ? null
              : event.response.contextState.activeResultSet,
          }
          : undefined;
        if (nextContextState) {
          setAgentContextState(nextContextState);
          writeActiveConversationId(nextContextState.conversationId);
        }
        setTurns((current) => current.map((turn) => turn.id === turnId ? {
          ...turn,
          status: 'complete',
          answer: event.response,
          search: event.response.search,
          partialAnswer: event.response.answer,
          toolExecutions: event.response.toolExecutions,
          sources: event.response.sources,
        } : turn));
      } else if (event.type === 'run_failed' && event.response) {
        setTurns((current) => current.map((turn) => turn.id === turnId ? {
          ...turn,
          partialAnswer: event.response?.answer || turn.partialAnswer,
          search: event.response?.search || turn.search,
          sources: event.response?.sources || turn.sources,
        } : turn));
      }
    };

    try {
      const response = await runKnowledgeHubAgent(
        {
          query: nextQuery,
          history: history.slice(-10),
          activeContext,
          activeResultSet,
          conversationId: agentContextState?.conversationId,
          contextState: agentContextState,
          previousSources: agentContextState?.sources,
          depth: agentDepth,
        },
        handleAgentEvent,
        controller.signal,
      );
      updateTurn(turnId, { answer: response, search: response.search, status: 'complete' });
      const nextContextState = response.contextState
        ? {
          ...response.contextState,
          activeResultSet: responseStartsNewTopic(response)
            ? null
            : response.contextState.activeResultSet,
        }
        : undefined;
      if (nextContextState) {
        setAgentContextState(nextContextState);
        writeActiveConversationId(nextContextState.conversationId);
      }
      const responseResultSet = nextContextState?.activeResultSet || buildActiveResultSet(getRelevantResults(response.search.results, nextQuery));
      if (responseStartsNewTopic(response)) {
        setActiveResultSet(null);
      } else if (responseResultSet?.items.length > 0) {
        setActiveResultSet(responseResultSet);
      } else {
        setActiveResultSet(activeResultSet);
      }
      const nextHistoryItems: KnowledgeHubAnswerHistoryItem[] = [
        { role: 'user', content: nextQuery, query: nextQuery },
        { role: 'assistant', content: response.answer, query: nextQuery },
      ];
      setHistory((current) => [
        ...current,
        ...nextHistoryItems,
      ].slice(-10));
    } catch (error) {
      const anyError = error as any;
      if (anyError?.code === 'KNOWLEDGE_AGENT_DISABLED') {
        try {
          updateTurn(turnId, {
            status: 'searching',
            warningMessage: 'Athena Agent är inte aktiverad ännu. Legacy-flödet används.',
          });
          const legacySearch = await searchKnowledgeHubHybrid(nextQuery, 8);
          updateTurn(turnId, { search: legacySearch, status: 'answering' });
          const legacyResultSet = buildActiveResultSet(getRelevantResults(legacySearch.results, nextQuery));
          const response = await answerKnowledgeHubQuestion(
            nextQuery,
            8,
            history.slice(-8),
            activeContext,
            legacyResultSet.items.length > 0 ? legacyResultSet : activeResultSet,
          );
          updateTurn(turnId, {
            answer: response,
            search: response.search,
            status: 'complete',
            warningMessage: 'Athena Agent är inte aktiverad ännu. Legacy-flödet användes.',
          });
          const responseResultSet = buildActiveResultSet(getRelevantResults(response.search.results, nextQuery));
          setActiveResultSet(responseStartsNewTopic(response)
            ? null
            : responseResultSet.items.length > 0 ? responseResultSet : null);
          const legacyHistoryItems: KnowledgeHubAnswerHistoryItem[] = [
            { role: 'user', content: nextQuery, query: nextQuery },
            { role: 'assistant', content: response.answer, query: nextQuery },
          ];
          setHistory((current) => [
            ...current,
            ...legacyHistoryItems,
          ].slice(-10));
          return;
        } catch (legacyError) {
          updateTurn(turnId, { status: 'error', errorMessage: getErrorMessage(legacyError) });
          return;
        }
      }
      const errorMessage = getErrorMessage(error);
      if (controller.signal.aborted) {
        updateTurn(turnId, {
          status: 'warning',
          warningMessage: 'Athena-anropet avbröts. Redan hittade källor finns kvar.',
        });
      } else {
        updateTurn(turnId, {
          status: 'error',
          errorMessage,
        });
      }
    } finally {
      if (activeRunControllerRef.current === controller) {
        activeRunControllerRef.current = null;
      }
      setRunning(false);
    }
  };

  const resetConversation = () => {
    activeRunControllerRef.current?.abort();
    activeRunControllerRef.current = null;
    setHistoryMenuAnchor(null);
    setHistoryDialogOpen(false);
    setRunning(false);
    setConversationLoadAttempted(true);
    setHistory([]);
    setActiveContext(null);
    setActiveResultSet(null);
    setAgentContextState(undefined);
    setTurns([]);
    setMessage(null);
    writeActiveConversationId(undefined);
  };

  return (
    <>
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600, md: 680 },
          maxWidth: '100vw',
          bgcolor: '#f7f9fc',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            px: 2,
            py: 1.25,
            color: 'white',
            background: 'linear-gradient(135deg, #062b4c 0%, #0a5b8f 100%)',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 3,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.14)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  flexShrink: 0,
                }}
              >
                <AutoAwesomeIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 850, letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Chatta med Athena
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Athena väljer verktyg, hämtar källor och bearbetar svaret.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
              <IconButton
                onClick={(event) => setHistoryMenuAnchor(event.currentTarget)}
                sx={{ color: 'white' }}
                aria-label="Öppna chattmeny"
                aria-controls={historyMenuOpen ? 'knowledge-hub-chat-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={historyMenuOpen ? 'true' : undefined}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="knowledge-hub-chat-menu"
                anchorEl={historyMenuAnchor}
                open={historyMenuOpen}
                onClose={() => setHistoryMenuAnchor(null)}
                disablePortal
                slotProps={{
                  paper: {
                    sx: {
                      zIndex: (theme) => theme.zIndex.modal + 5,
                    },
                  },
                }}
              >
                <MenuItem onClick={resetConversation} disabled={!hasConversation && !activeContext && !activeResultSet}>
                  <AddCommentIcon fontSize="small" sx={{ mr: 1 }} />
                  Ny chatt
                </MenuItem>
                <MenuItem onClick={openHistoryDialog}>
                  <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
                  Historik
                </MenuItem>
              </Menu>
              <IconButton onClick={onClose} sx={{ color: 'white' }} aria-label="Stäng Chatta med Athena">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            p: 2,
            overflow: 'auto',
            flex: 1,
            background: 'linear-gradient(180deg, #f7f9fc 0%, #eef4fb 100%)',
          }}
        >
          <Stack spacing={2.25}>
            {message && <Alert severity={message.type}>{message.text}</Alert>}
            {restoringConversation && (
              <Alert icon={<CircularProgress size={18} />} severity="info" sx={{ borderRadius: 2 }}>
                Återställer Athena-chatten...
              </Alert>
            )}

            {!hasConversation && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2.25,
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.86)',
                  borderColor: '#dbeafe',
                  boxShadow: '0 20px 50px rgba(7,47,83,0.08)',
                }}
              >
                <Stack spacing={1.25}>
                  <Chip size="small" color="info" label="Ny chatt" sx={{ alignSelf: 'flex-start' }} />
                  <Typography variant="h5" sx={{ fontWeight: 850, letterSpacing: '-0.03em' }}>
                    Fråga Athena om produkter, recept eller miljöbilder.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Skriv naturligt, till exempel ett artikelnummer, en ingrediens, en produktfråga eller en bildbeskrivning.
                    Athena svarar med källor, thumbnails och objektkort från ditt indexerade material.
                  </Typography>
                </Stack>
              </Paper>
            )}

            {turns.map((turn, turnIndex) => (
              <Stack
                key={turn.id}
                spacing={1.25}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.65,
                      py: 1.15,
                      maxWidth: '82%',
                      borderRadius: '20px 20px 6px 20px',
                      bgcolor: '#0b5d91',
                      color: 'white',
                      boxShadow: '0 10px 26px rgba(11,93,145,0.24)',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.55 }}>
                      {turn.query}
                    </Typography>
                  </Paper>
                </Box>

                <Box
                  ref={turnIndex === turns.length - 1 ? latestTurnRef : undefined}
                  sx={{ display: 'flex', justifyContent: 'flex-start', scrollMarginTop: 16 }}
                >
                  <Stack spacing={1} sx={{ width: '100%', maxWidth: '94%' }}>
                    <AnswerBlock
                      turn={turn}
                      showDebugScores={showDebugScores}
                      onAsk={(nextQuestion) => void submitQuestion(nextQuestion)}
                      onUseAsContext={(entity) => setActiveContext(buildActiveContext(entity))}
                    />
                    {showDebugScores && turn.toolExecutions && turn.toolExecutions.length > 0 && (
                      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, bgcolor: '#f8fbff', borderColor: '#dbeafe' }}>
                        <Stack spacing={0.75}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                            Athenas arbete
                          </Typography>
                          {turn.toolExecutions.map((execution) => (
                            <Stack key={execution.callId} direction="row" spacing={1} alignItems="center">
                              {execution.status === 'running' && <CircularProgress size={14} />}
                              <Chip
                                size="small"
                                color={execution.status === 'failed' ? 'warning' : execution.status === 'completed' ? 'success' : 'info'}
                                variant="outlined"
                                label={execution.status === 'completed'
                                  ? `${execution.label}${typeof execution.resultCount === 'number' ? ` · ${execution.resultCount}` : ''}`
                                  : execution.status === 'failed'
                                    ? `${execution.label} · kunde inte slutföras`
                                    : `${execution.label}...`}
                              />
                            </Stack>
                          ))}
                        </Stack>
                      </Paper>
                    )}
                    {(turn.status === 'running' || turn.status === 'searching' || turn.status === 'answering') && !turn.answer && (
                      <Alert
                        icon={<CircularProgress size={18} />}
                        severity="info"
                        sx={{ borderRadius: 2, alignSelf: 'flex-start' }}
                      >
                        {turn.status === 'searching'
                          ? 'Söker källor...'
                          : turn.status === 'answering'
                            ? (turn.progressMessage || 'Sammanfattar utifrån källorna...')
                            : turn.progressMessage || turn.toolExecutions?.find((execution) => execution.status === 'running')?.label || 'Athena planerar nästa steg...'}
                      </Alert>
                    )}
                  </Stack>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: '#d7e3f0',
            bgcolor: 'rgba(255,255,255,0.96)',
            boxShadow: '0 -16px 40px rgba(7,47,83,0.08)',
          }}
        >
          <Stack spacing={1.25}>
            {(activeContext || resultSetSummary) && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  borderRadius: 2.5,
                  bgcolor: '#f8fbff',
                  borderColor: '#dbeafe',
                }}
              >
                <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
                  {resultSetSummary && (
                    <>
                      <Chip size="small" color="info" label="Senaste lista" />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {resultSetSummary}
                      </Typography>
                    </>
                  )}
                  {activeContext?.title && (
                    <>
                      <Chip size="small" color="primary" variant="outlined" label="Fokuserar på" />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {activeContext.title}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setActiveContext(null)}
                        aria-label="Rensa fokus"
                        sx={{ color: 'text.secondary', ml: -0.5 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Stack>
              </Paper>
            )}

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                Sökläge
              </Typography>
              <Chip
                size="small"
                color={agentDepth === 'standard' ? 'primary' : 'default'}
                variant={agentDepth === 'standard' ? 'filled' : 'outlined'}
                label="Standard"
                disabled={busy}
                onClick={() => setAgentDepth('standard')}
                sx={{ cursor: busy ? 'default' : 'pointer' }}
              />
              <Chip
                size="small"
                color={agentDepth === 'deep' ? 'primary' : 'default'}
                variant={agentDepth === 'deep' ? 'filled' : 'outlined'}
                label="Djup sökning"
                disabled={busy}
                onClick={() => setAgentDepth('deep')}
                sx={{ cursor: busy ? 'default' : 'pointer' }}
              />
              <Typography variant="caption" color="text.secondary">
                {agentDepth === 'deep' ? 'Letar bredare och kan ta längre tid.' : 'Snabbare svar med begränsad retrieval.'}
              </Typography>
            </Stack>

            <TextField
              placeholder="Skriv en fråga till Athena..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void submitQuestion();
                }
              }}
              minRows={1}
              maxRows={4}
              multiline
              fullWidth
              disabled={busy}
              sx={{
                '& .MuiOutlinedInput-root': {
                  alignItems: 'flex-end',
                  borderRadius: 3,
                  bgcolor: '#ffffff',
                  pr: 1,
                },
              }}
              InputProps={{
                endAdornment: (
                  <Stack direction="row" spacing={0.75} sx={{ ml: 1, mb: 0.25 }}>
                    {busy && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<StopCircleOutlinedIcon />}
                        onClick={() => activeRunControllerRef.current?.abort()}
                        sx={{ borderRadius: 2.5, whiteSpace: 'nowrap' }}
                      >
                        Stoppa
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      endIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                      disabled={!query.trim() || busy}
                      onClick={() => void submitQuestion()}
                      sx={{ borderRadius: 2.5, whiteSpace: 'nowrap' }}
                    >
                      Skicka
                    </Button>
                  </Stack>
                ),
              }}
            />

            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {busy ? 'Athena arbetar med frågan...' : 'Exempel'}
              </Typography>
              {EXAMPLE_QUERIES.map((example) => (
                <Chip
                  key={example}
                  size="small"
                  variant="outlined"
                  label={example}
                  disabled={busy}
                  onClick={() => void submitQuestion(example)}
                  sx={{ cursor: busy ? 'default' : 'pointer', bgcolor: '#ffffff' }}
                />
              ))}
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Drawer>
    <Dialog
      open={historyDialogOpen}
      onClose={() => setHistoryDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 6 }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 850 }}>
              Chatthistorik
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Senaste 20 Athena-konversationerna.
            </Typography>
          </Box>
          <IconButton onClick={() => setHistoryDialogOpen(false)} aria-label="Stäng historik">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.25}>
          {historyError && <Alert severity="error">{historyError}</Alert>}
          {historyLoading ? (
            <Alert icon={<CircularProgress size={18} />} severity="info">
              Hämtar historik...
            </Alert>
          ) : conversationSummaries.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#f8fbff' }}>
              <Typography variant="body2" color="text.secondary">
                Det finns inga sparade Athena-konversationer ännu.
              </Typography>
            </Paper>
          ) : (
            <List disablePadding>
              {conversationSummaries.map((conversation, index) => (
                <React.Fragment key={conversation.conversationId}>
                  {index > 0 && <Divider component="li" />}
                  <ListItemButton
                    disabled={restoringConversation}
                    onClick={() => void loadConversationFromHistory(conversation.conversationId)}
                    sx={{ borderRadius: 2, py: 1.1 }}
                  >
                    <ListItemText
                      primary={conversation.title || 'Athena-konversation'}
                      secondary={[
                        formatConversationDate(conversation.updatedAt),
                        conversation.messageCount ? `${conversation.messageCount} meddelanden` : '',
                        conversation.preview,
                      ].filter(Boolean).join(' · ')}
                      primaryTypographyProps={{
                        fontWeight: 800,
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void loadConversationSummaries()} disabled={historyLoading}>
          Uppdatera
        </Button>
        <Button onClick={() => setHistoryDialogOpen(false)}>
          Stäng
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export const knowledgeHubDrawerInternals = {
  getRelevantResults,
  extractCitedReferenceNumbers,
  getSourceReferenceNumbers,
  getReferencedResults,
  responseStartsNewTopic,
  getMaterialGroupKey,
  getMaterialSectionTitle,
  queryLooksVisual,
  queryLooksDocumentFocused,
  ReferenceLinkedText,
  AnswerBlock,
  getDocumentViewerPath,
  upsertAgentToolExecution,
  upsertAgentSearchResult,
};

export default KnowledgeHubDrawer;
